import { useState, useEffect, useRef } from "react";
import { Box, Typography, Stack, IconButton } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { motion, AnimatePresence } from "framer-motion";

const getDirection = (from, to, total) => {
	if (from === total - 1 && to === 0) return 1;
	if (from === 0 && to === total - 1) return -1;
	return to > from ? 1 : -1;
};

const MessageCarousel = ({ data }) => {
	const [index, setIndex] = useState(0);
	const [direction, setDirection] = useState(1);
	const [width, setWidth] = useState(0);
	const containerRef = useRef(null);
	const timeoutRef = useRef();

	useEffect(() => {
		if (containerRef.current) {
			setWidth(containerRef.current.offsetWidth);
		}
	}, []);

	useEffect(() => {
		if (data.length > 0 && index >= data.length) {
			setIndex(0);
		}
	}, [data.length, index]);

	useEffect(() => {
		if (data.length === 0) return; // Don't start animation if no data

		timeoutRef.current = setTimeout(() => {
			const nextIndex = (index + 1) % data.length;
			setDirection(getDirection(index, nextIndex, data.length));
			setIndex(nextIndex);
		}, 5000);

		return () => clearTimeout(timeoutRef.current);
	}, [index, data.length]);

	const slideVariants = {
		enter: (dir) => ({
			x: dir > 0 ? width : -width,
			opacity: 0,
		}),
		center: {
			x: 0,
			opacity: 1,
		},
		exit: (dir) => ({
			x: dir < 0 ? width : -width,
			opacity: 0,
		}),
	};

	const handleDotClick = (newIndex) => {
		if (newIndex === index) return;
		setDirection(getDirection(index, newIndex, data.length));
		setIndex(newIndex);
	};

	if (data.length === 0) return null;

	return (
		<Box
			ref={containerRef}
			sx={{
				width: "100%",
				maxWidth: 500,
				overflow: "hidden",
			}}
		>
			<AnimatePresence custom={direction} mode="wait">
				<motion.div
					key={data[index]?.id}
					custom={direction}
					variants={slideVariants}
					initial="enter"
					animate="center"
					exit="exit"
					transition={{ duration: 0.5 }}
					layout
					style={{
						width: "100%",
					}}
				>
					<Stack>
						<Typography
							variant="p"
							sx={{
								fontSize: "1.125rem",
								lineHeight: "2rem",
								fontWeight: 700,
								color: "#fff",
							}}
						>
							{data[index]?.title}
						</Typography>
						<Typography
							variant="p"
							sx={{
								fontSize: "1.125rem",
								lineHeight: "2rem",
								fontWeight: 500,
								color: "#fff",
							}}
						>
							{data[index]?.description}
						</Typography>
					</Stack>
				</motion.div>
			</AnimatePresence>

			<Stack direction="row" spacing={1} justifyContent="center" mt={2}>
				{data.map((_, i) => (
					<IconButton
						key={i}
						size="small"
						onClick={() => handleDotClick(i)}
						sx={{ color: i === index ? "#f2f2f2" : "#49787F" }}
					>
						<FiberManualRecordIcon
							sx={{ width: "0.75rem", height: "0.75rem" }}
						/>
					</IconButton>
				))}
			</Stack>
		</Box>
	);
};

export default MessageCarousel;
